import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Plus, 
  Edit, 
  Users,
  LogOut,
  User,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import SEO from '../../components/SEO';

const CausesDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [petitions, setPetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('causes_token');
    const userData = localStorage.getItem('causes_user');
    
    if (!token || !userData) {
      toast.error('Please login to access dashboard');
      navigate('/causes/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    fetchPetitions(parsedUser.id);
  }, [navigate]);

  const fetchPetitions = async (campaignerId) => {
    const token = localStorage.getItem('causes_token');
    
    try {
      const response = await axios.get(
        `${API}/api/petitions/campaigner/${campaignerId}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      setPetitions(response.data);
    } catch (error) {
      console.error('Error fetching petitions:', error);
      toast.error('Failed to load your petitions');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('causes_token');
    localStorage.removeItem('causes_user');
    toast.success('Logged out successfully');
    navigate('/causes');
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: {
        icon: <Edit size={20} />,
        label: 'Draft',
        color: '#95a5a6',
        bgColor: '#ecf0f1'
      },
      pending_review: {
        icon: <Clock size={20} />,
        label: 'Under Review',
        color: '#f39c12',
        bgColor: '#fef3e0'
      },
      changes_requested: {
        icon: <AlertCircle size={20} />,
        label: 'Changes Needed',
        color: '#e74c3c',
        bgColor: '#fdecea'
      },
      approved: {
        icon: <CheckCircle size={20} />,
        label: 'Live',
        color: '#27ae60',
        bgColor: '#e8f5e9'
      },
      rejected: {
        icon: <XCircle size={20} />,
        label: 'Rejected',
        color: '#c0392b',
        bgColor: '#f8d7da'
      },
      resolved: {
        icon: <CheckCircle size={20} />,
        label: 'Resolved',
        color: '#2196f3',
        bgColor: '#e3f2fd'
      }
    };
    return configs[status] || configs.draft;
  };

  if (!user) return null;

  return (
    <div className="causes-dashboard-page">
      <SEO title="My Dashboard - Elderra Causes" description="Manage your petitions" />

      {/* Header */}
      <div className="dashboard-header">
        <div className="container">
          <div className="dashboard-header-content">
            <div className="dashboard-user-info">
              <div className="user-avatar">
                <User size={32} />
              </div>
              <div>
                <h1 className="dashboard-title">Welcome, {user.name || 'User'}!</h1>
                <p className="dashboard-subtitle">Manage your petitions and track progress</p>
              </div>
            </div>
            <div className="dashboard-actions">
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="btn-logout"
              >
                <LogOut size={20} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        <div className="container">
          {/* Quick Actions */}
          <div className="dashboard-quick-actions">
            <Link to="/causes/start" className="quick-action-card primary">
              <Plus size={48} />
              <h3>Start New Petition</h3>
              <p>Create a petition for your cause</p>
            </Link>

            <div className="quick-action-card stats">
              <FileText size={48} color="#e5266a" />
              <h3>{petitions.length}</h3>
              <p>Your Petitions</p>
            </div>

            <div className="quick-action-card stats">
              <Users size={48} color="#2196f3" />
              <h3>{petitions.reduce((sum, p) => sum + (p.signature_count || 0), 0)}</h3>
              <p>Total Signatures</p>
            </div>
          </div>

          {/* Petitions List */}
          <div className="dashboard-petitions-section">
            <h2 className="section-title-large">Your Petitions</h2>

            {loading ? (
              <div className="loading-message-large">
                <FileText size={64} color="#e5266a" />
                <p>Loading your petitions...</p>
              </div>
            ) : petitions.length === 0 ? (
              <div className="empty-state-large">
                <FileText size={80} color="#e0e0e0" />
                <h3>No Petitions Yet</h3>
                <p>Start your first petition to make your voice heard!</p>
                <Link to="/causes/start" className="btn-primary-large">
                  <Plus size={24} />
                  Create Your First Petition
                </Link>
              </div>
            ) : (
              <div className="petitions-list-large">
                {petitions.map((petition) => {
                  const statusConfig = getStatusConfig(petition.status);
                  
                  return (
                    <div key={petition.id} className="petition-card-large">
                      <div className="petition-card-header-large">
                        <div 
                          className="petition-status-large"
                          style={{ 
                            backgroundColor: statusConfig.bgColor,
                            color: statusConfig.color
                          }}
                        >
                          {statusConfig.icon}
                          <span>{statusConfig.label}</span>
                        </div>
                        
                        {petition.signature_count > 0 && (
                          <div className="petition-signatures-large">
                            <Users size={24} />
                            <span>{petition.signature_count} signatures</span>
                          </div>
                        )}
                      </div>

                      <h3 className="petition-title-large">{petition.title}</h3>

                      {petition.target_person && (
                        <p className="petition-target-large">
                          To: {petition.target_person}
                        </p>
                      )}

                      <p className="petition-excerpt-large">
                        {petition.grievance_details.substring(0, 200)}...
                      </p>

                      <div className="petition-card-footer-large">
                        {(petition.status === 'draft' || petition.status === 'changes_requested') && (
                          <Button 
                            onClick={() => navigate(`/causes/edit/${petition.id}`)}
                            className="btn-edit-large"
                          >
                            <Edit size={20} />
                            Edit Petition
                          </Button>
                        )}

                        {petition.status === 'approved' && (
                          <Button 
                            onClick={() => navigate(`/causes/petition/${petition.id}`)}
                            className="btn-view-large"
                          >
                            <FileText size={20} />
                            View Public Page
                          </Button>
                        )}

                        {petition.status === 'changes_requested' && petition.admin_notes && petition.admin_notes.length > 0 && (
                          <div className="admin-feedback-large">
                            <AlertCircle size={20} />
                            <div>
                              <strong>Admin Feedback:</strong>
                              <p>{petition.admin_notes[petition.admin_notes.length - 1].note}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CausesDashboard;
